import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Label } from '@strategy4kids/ui';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../services/api.js';

const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 5000;

const INPUT_CLASS =
  'flex w-full rounded-xl border border-border bg-surface px-4 text-base text-fg ' +
  'placeholder:text-fg-muted/70 transition-[border-color,box-shadow] duration-150 ' +
  'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** What the parent sees for each way a send can fail, by HTTP status. */
function failureMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 429) return 'You have sent a few messages already. Please try again in a while.';
    if (err.status === 400) return 'Please add a subject and a description (not too long).';
    if (err.status === 503) return 'Support is not available yet.';
  }
  return 'We could not send that just now. Please try again later.';
}

/**
 * "Contact support" for the signed-in PARENT. Not shown to kids on purpose: players
 * are anonymous device tokens, so a ticket form there would send a child's words
 * to a support system with no accountable adult.
 *
 * Renders nothing until the server says a support inbox is configured, so it is
 * invisible (and sends nothing) until the operator provisions the tenant key. The
 * ticket is created on the server under the parent's session; the browser only
 * supplies the subject, description and where it was sent from.
 *
 * Built on the design system's Dialog and not `@blueforge-studio/support-kit`'s
 * SupportButton: that component hard-codes its fetch with no way to add the Bearer
 * token this app's parent auth needs, and this app routes every request through
 * `services/api.ts`.
 */
export interface SupportPrefill {
  readonly subject: string;
  readonly description: string;
}

/**
 * With `report` set the dialog opens at once with that subject and description and the "Contact support" button is not drawn (the caller has
 * its own button, e.g. "Report this pack"); `onClose` fires when the dialog is closed. Without it nothing changes.
 */
export function ParentSupport({ token, report, onClose }: { token: string; report?: SupportPrefill | null; onClose?: () => void }) {
  const [configured, setConfigured] = useState(false);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .supportStatus(token)
      .then((s) => {
        if (live) setConfigured(s.configured === true);
      })
      .catch(() => {
        // Unreachable or signed out: stay hidden rather than offer a dead button.
        if (live) setConfigured(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  useEffect(() => {
    if (!report) return;
    setSubject(report.subject);
    setDescription(report.description);
    setPhase('idle');
    setError(null);
    setTicketId(null);
    setOpen(true);
  }, [report]);

  if (!configured) return null;

  const canSend = subject.trim().length > 0 && description.trim().length > 0 && phase !== 'sending';

  const reset = () => {
    setSubject('');
    setDescription('');
    setPhase('idle');
    setError(null);
    setTicketId(null);
  };

  const send = async () => {
    if (!canSend) return;
    setPhase('sending');
    setError(null);
    try {
      const { id } = await api.createSupportTicket(token, {
        subject: subject.trim(),
        description: description.trim(),
        context: { route: window.location.pathname, url: window.location.href },
      });
      setTicketId(id);
      setPhase('sent');
    } catch (err) {
      setError(failureMessage(err));
      setPhase('error');
    }
  };

  return (
    <>
      {report ? null : (
        <Button
          variant="ghost"
          data-testid="support-button"
          data-appdom-id="support-button"
          data-appdom-type="Button"
          onClick={() => setOpen(true)}
        >
          Contact support
        </Button>
      )}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next && phase === 'sent') reset();
          if (!next) onClose?.();
        }}
      >
        <DialogContent data-testid="support-dialog">
          <DialogTitle>Contact support</DialogTitle>
          <DialogDescription>
            Tell us what is going on. We will use the email on your parent account to reply.
          </DialogDescription>
          {phase === 'sent' ? (
            <div className="mt-4 flex flex-col gap-3" data-testid="support-sent">
              <p className="font-bold text-fg">Thanks, we have your message.</p>
              {ticketId && <p className="text-sm text-fg-muted">Reference: {ticketId}</p>}
              <Button onClick={() => setOpen(false)} data-testid="support-done">
                Done
              </Button>
            </div>
          ) : (
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="support-subject">Subject</Label>
                <input
                  id="support-subject"
                  data-testid="support-subject"
                  className={`${INPUT_CLASS} h-11`}
                  value={subject}
                  maxLength={SUBJECT_MAX}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={phase === 'sending'}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="support-description">What happened?</Label>
                <textarea
                  id="support-description"
                  data-testid="support-description"
                  className={`${INPUT_CLASS} min-h-32 py-3`}
                  value={description}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={phase === 'sending'}
                />
              </div>
              {error && (
                <p className="font-bold text-red-600 dark:text-red-400" role="alert" data-testid="support-error">
                  {error}
                </p>
              )}
              <Button type="submit" loading={phase === 'sending'} disabled={!canSend} data-testid="support-send">
                Send
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
